import { Popover, Tag } from 'antd';
import { Clock3 } from 'lucide-react';
import type { PricingValues, PricingVersion } from '@/types';
import {
  describePricingBand,
  resolvePricingValue,
} from '../pricingPresentation';

const priceFields: Array<{
  key: keyof PricingValues;
  label: string;
  unit: string;
}> = [
  { key: 'input_usd_per_million_tokens', label: '输入', unit: '/M' },
  { key: 'output_usd_per_million_tokens', label: '输出', unit: '/M' },
  { key: 'cache_read_usd_per_million_tokens', label: 'Cache 读', unit: '/M' },
  {
    key: 'cache_write_usd_per_million_tokens',
    label: 'Cache 写',
    unit: '/M',
  },
  { key: 'image_usd_per_million_tokens', label: '图片', unit: '/M' },
  { key: 'audio_usd_per_million_tokens', label: '音频', unit: '/M' },
  { key: 'fixed_price_usd', label: '固定价', unit: '/次' },
];

function PriceGrid({
  prices,
  basePrices,
}: {
  prices: PricingValues;
  basePrices: PricingValues;
}) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-5 gap-y-1 text-xs">
      {priceFields.map(({ key, label, unit }) => {
        const resolved = resolvePricingValue(prices, basePrices, key);
        return (
          <div key={key} className="flex justify-between gap-4">
            <span className="text-muted-foreground">{label}</span>
            <span className="text-right font-mono">
              {resolved.value == null ? '未配置' : `$${resolved.value}${unit}`}
              {resolved.inherited ? (
                <span className="ml-1 font-sans text-muted-foreground">
                  （继承基础价）
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function VersionDetails({
  version,
  basePrices,
}: {
  version: PricingVersion;
  basePrices: PricingValues;
}) {
  return (
    <section className="space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-xs font-medium">{version.id}</span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock3 className="h-3.5 w-3.5" />
          {version.effective_from}
        </span>
      </div>
      <div className="space-y-2">
        {version.price_bands.map((band) => (
          <div key={band.id} className="rounded bg-muted/40 p-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Tag color={band.default ? 'default' : 'blue'}>{band.id}</Tag>
              <span>{describePricingBand(band)}</span>
              {band.priority != null ? (
                <span className="text-muted-foreground">
                  优先级 {band.priority}
                </span>
              ) : null}
            </div>
            <PriceGrid prices={band.prices} basePrices={basePrices} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function PricingVersionsDisplay({
  versions,
  basePrices,
}: {
  versions?: PricingVersion[];
  basePrices: PricingValues;
}) {
  if (!versions?.length) {
    return <span className="text-muted-foreground">固定价格</span>;
  }
  const bandCount = versions.reduce(
    (total, version) => total + version.price_bands.length,
    0,
  );
  const modes = new Set(
    versions.flatMap((version) =>
      version.price_bands
        .map((band) => band.match?.mode)
        .filter((mode): mode is string => Boolean(mode)),
    ),
  );

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      content={
        <div className="max-h-[520px] w-[680px] max-w-[80vw] space-y-3 overflow-auto p-1">
          <div className="text-xs text-muted-foreground">
            Token 单价单位为 USD / 百万 tokens，固定价单位为 USD /
            次；以下为应用规则倍率前的配置值。
          </div>
          {versions.map((version) => (
            <VersionDetails
              key={version.id}
              version={version}
              basePrices={basePrices}
            />
          ))}
        </div>
      }
    >
      <button
        type="button"
        className="flex cursor-pointer flex-col items-start gap-1 text-left"
        aria-label={`查看 ${versions.length} 个动态价格版本`}
      >
        <span>
          <Tag color="blue">{versions.length} 个版本</Tag>
          <span className="text-xs text-muted-foreground">{bandCount} 档</span>
        </span>
        <span className="flex flex-wrap gap-1">
          {[...modes].map((mode) => (
            <Tag key={mode}>{mode}</Tag>
          ))}
        </span>
      </button>
    </Popover>
  );
}
